import {
  Button,
  DialogActions,
  ListItemIcon,
  ListItemText,
  MenuItem,
  type DialogActionsProps,
} from '@mui/material';
import type { TriggerReportJobRequestPayload } from '@vy/api-definition';
import { VyIcon } from '@vy/ui-design-system';
import type { ReportJobTypes } from '@vy/types';
import { REPORT_JOB_TYPES } from '@vy/types';
import { isNullOrUndefined, raiseUnreachable } from '@vy/utils';
import React from 'react';
import { VyDialog } from '../../../../../components/new-design/feedback/VyDialog';
import { useTriggerReportJob } from '../../../../../hooks/requests/use-trigger-report-job';
import { useDialog } from '../../../../../hooks/use-dialog';
import { runMutation } from '../../../../../utils/vy-mutation';

export type TriggerReportJobFormProps = {
  onSubmit: (payload: TriggerReportJobRequestPayload) => void;
  dialogActions: React.ReactElement<DialogActionsProps>;
};

type TriggerReportJobDialogProps = {
  formContent?: (props: TriggerReportJobFormProps) => React.ReactElement<TriggerReportJobFormProps>;
  reportJobType?: ReportJobTypes;
  disabled?: boolean;
};

export const TriggerReportJobForm: React.FC<TriggerReportJobDialogProps> = ({
  reportJobType,
  formContent,
  disabled = false,
}) => {
  const { isDialogOpen, handleOpen, handleClose } = useDialog();
  const triggerReportJobMutation = useTriggerReportJob();

  const handleFormSubmit = async (payload: TriggerReportJobRequestPayload) => {
    await runMutation(
      triggerReportJobMutation,
      payload,
      {
        pending: 'Requesting...',
        success: 'Report requested',
        error: 'Failed to request report',
      },
      {
        onSuccess: handleClose,
      },
    );
  };

  return (
    <>
      <MenuItem
        sx={{ height: '50px !important' }}
        disabled={isNullOrUndefined(reportJobType) || disabled}
        onClick={handleOpen}
      >
        <ListItemIcon>
          <VyIcon
            icon="Notebook"
            style={{
              transform: 'scale(0.8)',
            }}
          />
        </ListItemIcon>
        <ListItemText> Request a report</ListItemText>
      </MenuItem>

      <VyDialog
        open={isDialogOpen}
        onClose={handleClose}
        label={`Request a ${reportJobType ? getReportTypeLabel(reportJobType) : ''} report`}
      >
        {formContent?.({
          onSubmit: handleFormSubmit,
          dialogActions: (
            <DialogActions>
              <Button variant="contained" color="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Submit
              </Button>
            </DialogActions>
          ),
        })}
      </VyDialog>
    </>
  );
};

function getReportTypeLabel(reportType: ReportJobTypes): string {
  switch (reportType) {
    case REPORT_JOB_TYPES.CommitmentUsage:
      return 'commitment usage';
    case REPORT_JOB_TYPES.UnchargedUsage:
      return 'uncharged usage';
    case REPORT_JOB_TYPES.ProductsUsage:
      return 'product usage';
    case REPORT_JOB_TYPES.CustomerRevenue:
      return 'customer revenue';
    case REPORT_JOB_TYPES.Contracts:
      return 'contracts';
    case REPORT_JOB_TYPES.AnonymousCustomers:
      return 'anonymous customers';
    case REPORT_JOB_TYPES.MonthlyClosure:
      return 'monthly closure';
    case REPORT_JOB_TYPES.Customers:
      return 'customers';
    case REPORT_JOB_TYPES.UpcomingInvoices:
      return 'upcoming invoices';
    case REPORT_JOB_TYPES.UnapprovedInvoices:
      return 'unapproved invoices';
    case REPORT_JOB_TYPES.FailedInvoiceDeliveries:
      return 'failed invoice deliveries';
    case REPORT_JOB_TYPES.Usage:
      return 'usage';
    case REPORT_JOB_TYPES.RecurringRevenue:
      return 'recurring revenue';
    default:
      return raiseUnreachable(reportType);
  }
}
